import { ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography
} from '@mui/material';
import { addMonths, format, getDay, getDaysInMonth, startOfMonth, subMonths } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useEffect, useState } from 'react';
import { tasksAPI } from '../services/api';

export default function Calendar() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      const response = await tasksAPI.getAll();
      setTasks(response.data.filter(t => t.deadline));
    } catch (err) {
      setError('Ошибка загрузки задач');
    } finally {
      setLoading(false);
    }
  };

  const getTasksForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return tasks.filter(task => {
      const taskDate = task.deadline.split('T')[0];
      return taskDate === dateStr;
    });
  };

  const handleDateClick = (date, dayTasks) => {
    if (dayTasks.length > 0) {
      setSelectedDate(date);
      setDialogOpen(true);
    }
  };

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDayOfMonth = startOfMonth(currentDate);
    const startDayOfWeek = getDay(firstDayOfMonth);

    const days = [];
    const weekDays = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

    // Header
    days.push(
      <Box key="header" display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1} mb={1}>
        {weekDays.map((day, i) => (
          <Box key={i} textAlign="center" fontWeight="bold" color="text.secondary" p={1}>
            {day}
          </Box>
        ))}
      </Box>
    );

    // Empty cells before first day
    const cells = [];
    for (let i = 0; i < startDayOfWeek; i++) {
      cells.push(<Box key={`empty-${i}`} sx={{ bgcolor: 'grey.50', minHeight: 100, p: 1, borderRadius: 1 }} />);
    }

    // Days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dayTasks = getTasksForDate(date);
      const isToday = format(date, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

      cells.push(
        <Box
          key={day}
          onClick={() => handleDateClick(date, dayTasks)}
          sx={{
            bgcolor: isToday ? 'primary.50' : 'white',
            border: 1,
            borderColor: isToday ? 'primary.main' : 'grey.200',
            minHeight: 100,
            p: 1,
            borderRadius: 1,
            cursor: dayTasks.length > 0 ? 'pointer' : 'default',
            '&:hover': { bgcolor: dayTasks.length > 0 ? 'action.hover' : 'grey.50' }
          }}
        >
          <Typography variant="body2" fontWeight={isToday ? 'bold' : 'normal'} mb={0.5}>
            {day}
          </Typography>
          {dayTasks.length > 0 && (
            <Chip
              label={`${dayTasks.length} ${dayTasks.length === 1 ? 'задача' : 'задач'}`}
              size="small"
              color="primary"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>
      );
    }

    days.push(
      <Box key="days" display="grid" gridTemplateColumns="repeat(7, 1fr)" gap={1}>
        {cells}
      </Box>
    );

    return days;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <IconButton onClick={() => setCurrentDate(subMonths(currentDate, 1))}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold">
              {format(currentDate, 'LLLL yyyy', { locale: ru })}
            </Typography>
            <IconButton onClick={() => setCurrentDate(addMonths(currentDate, 1))}>
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card>
        <CardContent>
          {renderCalendar()}
        </CardContent>
      </Card>

      {tasks.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Нет задач с дедлайнами. Добавьте задачи с датами в разделе "Задачи"
        </Alert>
      )}

      {/* Dialog для показа задач на выбранную дату */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Задачи на {selectedDate && format(selectedDate, 'd MMMM yyyy', { locale: ru })}
            </Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <List>
            {selectedDate && getTasksForDate(selectedDate).map(task => (
              <ListItem key={task.id} divider>
                <ListItemText
                  primary={task.description}
                  secondary={
                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                      <Chip
                        label={task.status?.name}
                        size="small"
                        sx={{ bgcolor: task.status?.color, color: 'white', fontSize: '0.7rem' }}
                      />
                      {task.client && (
                        <Chip label={task.client.companyName} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {task.project && (
                        <Chip label={task.project.name} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                      {task.assignee && (
                        <Chip label={task.assignee.fullName} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
